/**
 * Validation utilities
 * Shared validation functions used across the application
 */

const RESERVED_SLUGS = [
    'admin', 'api', 'auth', 'cart', 'catalog', 'checkout', 'config', 
    'help', 'home', 'login', 'logout', 'tiendas', 'productos', 
    'servicios', 'search', 'user', 'profile', 'order', 'payment',
    'partner', 'partners', 'shop', 'shops', 'store', 'stores',
    'review', 'reviews', 'feedback', 'rating', 'ratings',
    'dashboard', 'panel', 'control', 'settings', 'configuracion',
    'support', 'soporte', 'contact', 'contacto', 'about', 'about-us',
    'terms', 'privacy', 'legal', 'faq', 'help', 'ayuda',
    'api', 'graphql', 'rest', 'webhook', 'webhooks',
    'app', 'mobile', 'download', 'downloads',
    'blog', 'news', 'articles', 'post', 'posts',
    'category', 'categories', 'tag', 'tags',
    'brand', 'brands', 'vendor', 'vendors', 'seller', 'sellers'
];

/**
 * Validates if a string is a valid URL slug
 * - Only lowercase letters, numbers, and hyphens
 * - Must start and end with alphanumeric
 * - Length between 3 and 50 characters
 */
export function isValidSlug(slug: string): boolean {
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    return slugRegex.test(slug) && slug.length >= 3 && slug.length <= 50;
}

/**
 * Checks if a slug is reserved (cannot be used)
 */
export function isReservedSlug(slug: string): boolean {
    return RESERVED_SLUGS.includes(slug.toLowerCase());
}

/**
 * Validates an email address format
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Validates a Chilean RUT (Rol Único Tributario)
 */
export function isValidRut(rut: string): boolean {
    if (!rut || rut.length < 8) return false;
    
    // Remove dots and convert to lowercase
    const cleanRut = rut.replace(/\./g, '').toLowerCase();
    
    // Split RUT and verification digit
    const rutBody = cleanRut.slice(0, -1);
    const verificationDigit = cleanRut.slice(-1);
    
    if (!/^\d+$/.test(rutBody)) return false;
    
    // Calculate verification digit
    let sum = 0;
    let multiplier = 2;
    
    for (let i = rutBody.length - 1; i >= 0; i--) {
        sum += parseInt(rutBody[i]) * multiplier;
        multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    
    const expectedDigit = 11 - (sum % 11);
    let calculatedDigit: string;
    
    if (expectedDigit === 11) {
        calculatedDigit = '0';
    } else if (expectedDigit === 10) {
        calculatedDigit = 'k';
    } else {
        calculatedDigit = expectedDigit.toString();
    }
    
    return calculatedDigit === verificationDigit;
}

/**
 * Validates a Chilean phone number
 */
export function isValidPhone(phone: string): boolean {
    const cleanPhone = phone.replace(/\s/g, '').replace(/^\+56/, '');
    const phoneRegex = /^9\d{8}$/;
    return phoneRegex.test(cleanPhone);
}

/**
 * Sanitizes a string to create a valid slug
 * Consolidated version with NFD normalization for accents
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 50);
}
